import { Stack } from '@mui/material'
import { AxiosResponse } from 'axios'
import { useModal } from 'mui-modal-provider'
import { useTranslation } from 'react-i18next'
import { EFTypography, ConfirmationPopup, ErrorPopupContent } from '~/components'
import { ALL_NS } from '~/i18n/namespaces'
import { IFileUploadErrorMessage } from './useFileUploadMutation'
import { ButtonType } from '~/components/popups/DialogFooter/Button.model'

export function useHandleError() {
  const { t } = useTranslation(ALL_NS)
  const { showModal } = useModal()
  const showAxiosErrorDialog = (error: any): Promise<ButtonType | undefined> => {
    return new Promise<ButtonType | undefined>((resolve, reject) => {
      if (!error.message) {
        showModal(ConfirmationPopup, {
          title: t('MessageTitle_Error'),
          actionType: 'Close',
          children: <ErrorPopupContent error={error as AxiosResponse} />,
          onClose: action => {
            resolve(action) // Resolve the promise when the dialog is closed
          }
        })
      } else {
        resolve('CLOSE') // Resolve immediately if there's no dialog to show
      }
    })
  }

  const showFileUploadMessageDialog = (
    errors: IFileUploadErrorMessage[],
    title?: string,
    showFileName: boolean = true
  ) => {
    const dialogTitle =
      title ??
      (errors.length === 1 && errors[0].fileName // if only one file is uploaded and it is failed
        ? t('MessageTitle_Error')
        : t('MessageTitle_Message'))

    showModal(ConfirmationPopup, {
      title: dialogTitle,
      actionType: 'Close',
      width: 550,
      children: (
        <Stack
          maxHeight={300}
          overflow='auto'
        >
          {errors.map((error, index) => (
            <EFTypography
              my={1}
              key={error.fileName ?? '' + index}
            >
              {error.message ?? 'Upload failed'}{' '}
              {showFileName && error.fileName !== undefined
                ? `[ File: ${error.fileName}]`
                : ''}
            </EFTypography>
          ))}
        </Stack>
      ),
      onClose: () => {}
    })
  }

  const showErrorDialog = (message: string) => {
    showModal(ConfirmationPopup, {
      title: t('MessageTitle_Error'),
      actionType: 'Close',
      minWidth: 250,
      children: <EFTypography>{message}</EFTypography>,
      onClose: () => {}
    })
  }

  const showMessageDialog = (message: string, title?: string) => {
    showModal(ConfirmationPopup, {
      title: title ?? t('MessageTitle_Message'),
      actionType: 'Close',
      children: <EFTypography py={2}>{message}</EFTypography>,
      minWidth: 400,
      onClose: () => {}
    })
  }

  return {
    showAxiosErrorDialog,
    showFileUploadMessageDialog,
    showErrorDialog,
    showMessageDialog
  }
}
